import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import WishlistButton from '../components/WishlistButton'
import { useRecentlyViewed } from '../hooks/useRecentlyViewed'
import RecentlyViewed from '../components/RecentlyViewed'

interface Product {
  id: string
  name: string
  description: string
  price: number
  stock: number
  category: string
  image_urls: string[]
}

interface ProductVariant {
  id: string
  product_id: string
  variant_name: string
  variant_value: string
  price: number
  stock_quantity: number
  created_at: string
}

interface Review {
  id: string
  product_id: string
  customer_id: string
  rating: number
  review_text: string | null
  is_hidden: boolean
  created_at: string
}

interface ReviewStats {
  average_rating: number
  review_count: number
}

interface CanReviewResponse {
  can_review: boolean
  reason: 'eligible' | 'not_authenticated' | 'already_reviewed' | 'not_purchased'
}

type ReviewSortBy = 'recent' | 'highest' | 'lowest'

// Star Rating Component
function StarRating({
  rating,
  size = 'md',
  interactive = false,
  onRatingChange,
  hoverRating = 0
}: {
  rating: number
  size?: 'sm' | 'md' | 'lg'
  interactive?: boolean
  onRatingChange?: (rating: number) => void
  hoverRating?: number
}) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  }

  const displayRating = hoverRating > 0 ? hoverRating : rating

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRatingChange?.(star)}
          disabled={!interactive}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={star <= displayRating ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={star <= displayRating ? 0 : 1.5}
            className={`${sizes[size]} ${star <= displayRating ? 'text-yellow-400' : 'text-gray-300'} transition-colors`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
            />
          </svg>
        </button>
      ))}
    </div>
  )
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const { user } = useAuth()
  const { addToRecentlyViewed } = useRecentlyViewed()
  const [product, setProduct] = useState<Product | null>(null)
  const [variants, setVariants] = useState<ProductVariant[]>([])
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<string>('')
  const [addingToCart, setAddingToCart] = useState(false)
  const [cartMessage, setCartMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([])
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null)
  const [canReviewData, setCanReviewData] = useState<CanReviewResponse | null>(null)
  const [reviewSortBy, setReviewSortBy] = useState<ReviewSortBy>('recent')
  const [newRating, setNewRating] = useState(0)
  const [newReviewText, setNewReviewText] = useState('')
  const [submittingReview, setSubmittingReview] = useState(false)
  const [reviewMessage, setReviewMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [hoverRating, setHoverRating] = useState(0)

  useEffect(() => {
    if (id) {
      fetchProduct(id)
      fetchReviews(id)
      fetchReviewStats(id)
    }
  }, [id])

  // Track recently viewed products
  useEffect(() => {
    if (id && product) {
      addToRecentlyViewed(id)
    }
  }, [id, product, addToRecentlyViewed])

  // Fetch can review status when user or product changes
  useEffect(() => {
    if (id && user) {
      checkCanReview(id)
    } else {
      setCanReviewData(user ? null : { can_review: false, reason: 'not_authenticated' })
    }
  }, [id, user])

  const fetchProduct = async (productId: string) => {
    try {
      setLoading(true)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      // Fetch product and variants in parallel
      const [productResponse, variantsResponse] = await Promise.all([
        fetch(`${supabaseUrl}/rest/v1/products?id=eq.${productId}&select=*`, {
          method: 'GET',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(10000)
        }),
        fetch(`${supabaseUrl}/rest/v1/product_variants?product_id=eq.${productId}&select=*&order=variant_name,variant_value`, {
          method: 'GET',
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(10000)
        })
      ])

      if (!productResponse.ok) {
        throw new Error(`HTTP error! status: ${productResponse.status}`)
      }

      const productData = await productResponse.json()
      const variantsData = variantsResponse.ok ? await variantsResponse.json() : []

      if (productData && productData.length > 0) {
        setProduct(productData[0])
        setVariants(variantsData || [])
        setSelectedVariant(null)
        if (productData[0].image_urls && productData[0].image_urls.length > 0) {
          setSelectedImage(productData[0].image_urls[0])
        }
      } else {
        setProduct(null)
        setVariants([])
      }
    } catch (error) {
      console.error('Error fetching product:', error)
      setProduct(null)
      setVariants([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch reviews for the product
  const fetchReviews = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })

      if (error) throw error
      setReviews(data || [])
    } catch (error) {
      console.error('Error fetching reviews:', error)
      setReviews([])
    }
  }

  // Fetch review stats via RPC
  const fetchReviewStats = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_product_review_stats', { p_product_id: productId })

      if (error) throw error
      setReviewStats(data as ReviewStats)
    } catch (error) {
      console.error('Error fetching review stats:', error)
      setReviewStats(null)
    }
  }

  // Check if current user can review via RPC
  const checkCanReview = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('can_review_product', { p_product_id: productId })

      if (error) throw error
      setCanReviewData(data as CanReviewResponse)
    } catch (error) {
      console.error('Error checking can review:', error)
      setCanReviewData(null)
    }
  }

  // Submit a new review
  const handleSubmitReview = async () => {
    if (!product || !user || newRating === 0) return

    setSubmittingReview(true)
    setReviewMessage(null)

    try {
      const { error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: product.id,
          customer_id: user.id,
          rating: newRating,
          review_text: newReviewText.trim() || null
        })

      if (error) throw error

      setReviewMessage({ type: 'success', text: 'Thank you for your review!' })
      setNewRating(0)
      setNewReviewText('')
      setCanReviewData({ can_review: false, reason: 'already_reviewed' })

      // Refresh reviews and stats
      if (id) {
        fetchReviews(id)
        fetchReviewStats(id)
      }
    } catch (error) {
      console.error('Error submitting review:', error)
      setReviewMessage({ type: 'error', text: 'Failed to submit review. Please try again.' })
    } finally {
      setSubmittingReview(false)
    }
  }

  // Sort reviews
  const sortedReviews = useMemo(() => {
    const sorted = [...reviews]
    switch (reviewSortBy) {
      case 'highest':
        return sorted.sort((a, b) => b.rating - a.rating)
      case 'lowest':
        return sorted.sort((a, b) => a.rating - b.rating)
      case 'recent':
      default:
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    }
  }, [reviews, reviewSortBy])

  // Format date for reviews
  const formatReviewDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStockBadge = (stock: number) => {
    if (stock === 0) {
      return <span className="badge-sold-out">Sold Out</span>
    } else if (stock <= 5) {
      return <span className="badge-low-stock">Low Stock ({stock} left)</span>
    }
    return <span className="badge-in-stock">In Stock ({stock} available)</span>
  }

  // Group variants by variant_name for UI display
  const groupedVariants = variants.reduce((acc, variant) => {
    if (!acc[variant.variant_name]) {
      acc[variant.variant_name] = []
    }
    acc[variant.variant_name].push(variant)
    return acc
  }, {} as Record<string, ProductVariant[]>)

  // Check if variant selection is required
  const hasVariants = variants.length > 0
  const needsVariantSelection = hasVariants && !selectedVariant

  // Get the effective price and stock (from variant if selected, otherwise from product)
  const effectivePrice = selectedVariant ? selectedVariant.price : product?.price || 0
  const effectiveStock = selectedVariant ? selectedVariant.stock_quantity : (hasVariants ? 0 : (product?.stock || 0))

  const handleAddToCart = async () => {
    if (!product) return

    // If product has variants, require variant selection
    if (hasVariants && !selectedVariant) {
      setCartMessage({ type: 'error', text: 'Please select a variant' })
      return
    }

    setAddingToCart(true)
    setCartMessage(null)

    // Pass product data for guest cart support
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      image_urls: product.image_urls,
      stock: product.stock
    }

    const result = await addToCart(
      product.id,
      selectedVariant?.id || null,
      1,
      productData,
      selectedVariant || undefined
    )

    if (result.success) {
      setCartMessage({ type: 'success', text: 'Added to cart!' })
      // Clear success message after 3 seconds
      setTimeout(() => setCartMessage(null), 3000)
    } else {
      setCartMessage({ type: 'error', text: result.error || 'Failed to add to cart' })
    }

    setAddingToCart(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading product...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-8 h-8 text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Product not found</h3>
            <p className="text-gray-500 text-sm mb-6">The product you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/products')}
              className="px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Products
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Back Button */}
        <button
          onClick={() => navigate('/products')}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-6 transition-colors group text-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          <span className="font-medium">Back to Products</span>
        </button>

        {/* Product Detail Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Image Gallery Section - STRICTLY CONSTRAINED */}
            <div className="p-6 lg:p-8 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-100">
              {/* Main Image - MAX 280x280 */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center mb-4 overflow-hidden" style={{ minHeight: '18rem' }}>
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product.name}
                    className="img-product-detail"
                    style={{ maxWidth: '280px', maxHeight: '280px' }}
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1}
                      stroke="currentColor"
                      className="w-12 h-12 text-gray-300"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery - STRICTLY CONSTRAINED */}
              {product.image_urls && product.image_urls.length > 1 && (
                <div className="flex gap-2 justify-center flex-wrap">
                  {product.image_urls.map((imageUrl, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(imageUrl)}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                        selectedImage === imageUrl
                          ? 'border-purple-600 ring-2 ring-purple-100'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={`${product.name} - Image ${index + 1}`}
                        className="img-product-detail-thumb"
                        style={{ width: '56px', height: '56px', maxWidth: '56px', maxHeight: '56px' }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Information Section */}
            <div className="p-6 lg:p-8 flex flex-col">
              {/* Category */}
              <span className="inline-block px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-md mb-3 w-fit">
                {product.category}
              </span>

              {/* Product Name */}
              <h1 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">
                {product.name}
              </h1>

              {/* Price */}
              <p className="text-3xl font-bold text-gray-900 mb-4">
                {hasVariants && !selectedVariant ? (
                  // Show price range when variants exist but none selected
                  (() => {
                    const prices = variants.map(v => v.price)
                    const minPrice = Math.min(...prices)
                    const maxPrice = Math.max(...prices)
                    return minPrice === maxPrice
                      ? `$${minPrice.toFixed(2)}`
                      : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
                  })()
                ) : (
                  `$${effectivePrice.toFixed(2)}`
                )}
              </p>

              {/* Stock Status */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                {hasVariants ? (
                  selectedVariant ? (
                    getStockBadge(selectedVariant.stock_quantity)
                  ) : (
                    <span className="text-sm text-gray-500">Select a variant to see stock</span>
                  )
                ) : (
                  getStockBadge(product.stock)
                )}
              </div>

              {/* Variant Selection */}
              {hasVariants && (
                <div className="mb-6 pb-6 border-b border-gray-100">
                  {Object.entries(groupedVariants).map(([variantName, variantOptions]) => (
                    <div key={variantName} className="mb-4 last:mb-0">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{variantName}</h3>
                      <div className="flex flex-wrap gap-2">
                        {variantOptions.map((variant) => {
                          const isSelected = selectedVariant?.id === variant.id
                          const isOutOfStock = variant.stock_quantity === 0
                          return (
                            <button
                              key={variant.id}
                              onClick={() => setSelectedVariant(isSelected ? null : variant)}
                              disabled={isOutOfStock}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                isSelected
                                  ? 'bg-purple-600 text-white ring-2 ring-purple-600 ring-offset-2'
                                  : isOutOfStock
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              title={isOutOfStock ? 'Out of Stock' : `$${variant.price.toFixed(2)}`}
                            >
                              {variant.variant_value}
                              {isOutOfStock && <span className="ml-1 text-xs">(Out)</span>}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {selectedVariant && (
                    <p className="mt-3 text-sm text-gray-500">
                      Selected: <span className="font-medium text-gray-700">{selectedVariant.variant_name}: {selectedVariant.variant_value}</span>
                      <span className="mx-2">·</span>
                      <span className="font-medium text-gray-900">${selectedVariant.price.toFixed(2)}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="mb-8 flex-1">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">Description</h2>
                <p className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">
                  {product.description}
                </p>
              </div>

              {/* Cart Message */}
              {cartMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  cartMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {cartMessage.text}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Wishlist Button */}
                <WishlistButton productId={product.id} size="lg" className="border border-gray-200 flex-shrink-0" />

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={effectiveStock === 0 || addingToCart || needsVariantSelection}
                  className={`flex-1 py-3.5 px-6 rounded-xl font-semibold text-sm transition-all ${
                    effectiveStock > 0 && !addingToCart && !needsVariantSelection
                      ? 'bg-purple-600 text-white hover:bg-purple-700 active:scale-[0.98]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {addingToCart ? (
                    <span className="flex items-center justify-center">
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Adding...
                    </span>
                  ) : needsVariantSelection ? (
                    'Select a Variant'
                  ) : effectiveStock > 0 ? (
                    <span className="flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5 mr-2"
                        style={{ width: '20px', height: '20px', maxWidth: '20px', maxHeight: '20px' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                        />
                      </svg>
                      Add to Cart
                    </span>
                  ) : (
                    'Out of Stock'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-8">
          <div className="p-6 lg:p-8">
            {/* Reviews Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Customer Reviews</h2>
                {reviewStats && reviewStats.review_count > 0 && (
                  <div className="flex items-center gap-2">
                    <StarRating rating={Math.round(reviewStats.average_rating)} size="md" />
                    <span className="text-sm text-gray-600">
                      {reviewStats.average_rating.toFixed(1)} out of 5 ({reviewStats.review_count} review{reviewStats.review_count !== 1 ? 's' : ''})
                    </span>
                  </div>
                )}
              </div>

              {/* Sort Options */}
              {reviews.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Sort by:</span>
                  <div className="flex gap-1">
                    {[
                      { value: 'recent' as const, label: 'Most Recent' },
                      { value: 'highest' as const, label: 'Highest' },
                      { value: 'lowest' as const, label: 'Lowest' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setReviewSortBy(option.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          reviewSortBy === option.value
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Review Form or Status Message */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              {reviewMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  reviewMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {reviewMessage.text}
                </div>
              )}

              {canReviewData?.can_review ? (
                // User can review - show form
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Write a Review</h3>

                  {/* Star Rating Selector */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Your Rating</label>
                    <div
                      className="flex items-center gap-1"
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setNewRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          className="p-0.5"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill={star <= (hoverRating || newRating) ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            strokeWidth={star <= (hoverRating || newRating) ? 0 : 1.5}
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoverRating || newRating) ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                            />
                          </svg>
                        </button>
                      ))}
                      {newRating > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          {newRating} star{newRating !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Review Text */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Your Review (optional)</label>
                    <textarea
                      value={newReviewText}
                      onChange={(e) => setNewReviewText(e.target.value)}
                      placeholder="Share your experience with this product..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmitReview}
                    disabled={newRating === 0 || submittingReview}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      newRating > 0 && !submittingReview
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              ) : canReviewData?.reason === 'not_authenticated' ? (
                // Not logged in
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                      Sign in
                    </Link>
                    {' '}to leave a review
                  </p>
                </div>
              ) : canReviewData?.reason === 'not_purchased' ? (
                // Hasn't purchased
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">
                    Only customers who have purchased and received this product can leave a review.
                  </p>
                </div>
              ) : canReviewData?.reason === 'already_reviewed' ? (
                // Already reviewed
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">
                    You have already reviewed this product. Thank you for your feedback!
                  </p>
                </div>
              ) : null}
            </div>

            {/* Reviews List */}
            {sortedReviews.length > 0 ? (
              <div className="space-y-4">
                {sortedReviews.map((review) => (
                  <div key={review.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <StarRating rating={review.rating} size="sm" />
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Verified Purchase
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{formatReviewDate(review.created_at)}</span>
                    </div>
                    {review.review_text && (
                      <p className="text-sm text-gray-600 leading-relaxed">{review.review_text}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No reviews yet. Be the first to review this product!</p>
              </div>
            )}
          </div>
        </div>

        {/* Recently Viewed Products */}
        {id && <RecentlyViewed excludeProductId={id} title="Recently Viewed" />}
      </div>
    </div>
  )
}
